import * as React from 'react';

import type {Import2StatusFragment} from 'nutshell-graphql-types';

import {BulletedList} from '../bulleted-list/bulleted-list';
import {colors} from '../colors/colors';
// @ts-expect-error - imported from .js/.jsx file
import {CheckmarkIcon} from '../icon/svg/checkmark-icon';
// @ts-expect-error - imported from .js/.jsx file
import {LoadingIcon} from '../icon/svg/loading-icon';
import {WarningIcon} from '../icon/svg/warning-icon';
import {Body} from '../typography/body';

import {Logos} from './logos';

import styles from './status-page.module.css';

export const getCrmLogoFromType = (type: string) => {
    switch (type) {
        case 'ACT':
            return '/include/images/import/competitors/act.png';
        case 'ACTIVECAMPAIGN':
            return '/include/images/import/competitors/active-campaign.png';
        case 'AFFINITY':
            return '/include/images/import/competitors/affinity.png';
        case 'AGILECRM':
            return '/include/images/import/competitors/agilecrm.png';
        case 'ATTIO':
            return '/include/images/import/competitors/attio.png';
        case 'CAPSULE':
            return '/include/images/import/competitors/capsule.png';
        case 'CLOSE':
            return '/include/images/import/competitors/close.png';
        case 'CONSTANTCONTACT':
            return '/include/images/import/competitors/constant-contact.png';
        case 'COPERNIQ':
            return '/include/images/import/competitors/coperniq.png';
        case 'COPPER':
            return '/include/images/import/competitors/copper.png';
        case 'FIRMAO':
            return '/include/images/import/competitors/firmao.png';
        case 'FOWCRM':
            return '/include/images/import/competitors/fowcrm.png';
        case 'GOHIGHLEVEL':
            return '/include/images/import/competitors/highlevel.png';
        case 'HIGHRISE':
            return '/include/images/import/competitors/highrise.png';
        case 'HUBSPOT':
            return '/include/images/import/competitors/hubspot.png';
        case 'INSIGHTLY':
            return '/include/images/import/competitors/insightly.png';
        case 'KEAP':
            return '/include/images/import/competitors/keap.png';
        case 'LESSANNOYINGCRM':
            return '/include/images/import/competitors/lessannoyingcrm.png';
        case 'MSDYNAMICS':
            return '/include/images/import/competitors/microsoft-dynamics.png';
        case 'MAILCHIMP':
            return '/include/images/import/competitors/mailchimp.png';
        case 'NIMBLE':
            return '/include/images/import/competitors/nimble.png';
        case 'ONEPAGE':
            return '/include/images/import/competitors/one-page-crm.png';
        case 'PIPEDRIVE':
            return '/include/images/import/competitors/pipedrive.png';
        case 'PIPELINE':
            return '/include/images/import/competitors/pipeline.png';
        case 'REDTAIL':
            return '/include/images/import/competitors/redtail.png';
        case 'SALESFORCE':
            return '/include/images/import/competitors/salesforce.png';
        case 'STREAK':
            return '/include/images/import/competitors/streak.png';
        case 'SUGAR':
            return '/include/images/import/competitors/sugarcrm.png';
        case 'SUITE':
            return '/include/images/import/competitors/suitecrm.png';
        case 'WEALTHBOX':
            return '/include/images/import/competitors/wealthbox.png';
        case 'ZENDESKSELL':
            return '/include/images/import/competitors/zendesk-sell.png';
        case 'ZOHO':
            return '/include/images/import/competitors/zoho-crm.png';
        default:
            return '';
    }
};

export const getImportTypeFromCrmType = (type: string) => {
    switch (type) {
        case 'ACT':
            return 'data';
        case 'ACTIVECAMPAIGN':
            return 'contacts';
        case 'AGILECRM':
            return 'data';
        case 'ATTIO':
            return 'data';
        case 'CAPSULE':
            return 'data';
        case 'CLOSE':
            return 'data';
        case 'COPPER':
            return 'data';
        case 'FIRMAO':
            return 'data';
        case 'FOWCRM':
            return 'data';
        case 'GOHIGHLEVEL':
            return 'data';
        case 'HIGHRISE':
            return 'data';
        case 'HUBSPOT':
            return 'data';
        case 'INSIGHTLY':
            return 'data';
        case 'KEAP':
            return 'data';
        case 'LESSANNOYINGCRM':
            return 'data';
        case 'MSDYNAMICS':
            return 'data';
        case 'NIMBLE':
            return 'data';
        case 'ONEPAGE':
            return 'data';
        case 'PIPEDRIVE':
            return 'data';
        case 'PIPELINE':
            return 'data';
        case 'REDTAIL':
            return 'data';
        case 'SALESFORCE':
            return 'data';
        case 'STREAK':
            return 'data';
        case 'SUGAR':
            return 'data';
        case 'SUITE':
            return 'data';
        case 'WEALTHBOX':
            return 'data';
        case 'ZENDESKSELL':
            return 'contacts';
        case 'ZOHO':
            return 'data';
        default:
            return '';
    }
};

export const getImportStatusBadge = (status: any) => {
    switch (status) {
        case 'STATUS_NOT_STARTED':
            return {level: 'info', text: 'In progress'};
        case 'STATUS_MIGRATION_COMPLETE':
            return {level: 'success', text: 'Complete'};
        case 'STATUS_FAILURE':
            return {level: 'warning', text: 'Failure'};
        case 'STATUS_REVERTED':
            return {level: 'warning', text: 'Reverted'};
        default:
            return {level: 'info', text: 'In progress'};
    }
};

export const getStatusPageInfoFromImport2Import = (
    import2Import: Import2StatusFragment
): {
    icon: React.ReactNode;
    pageInfo: string;
    pageSubtitle: React.ReactNode;
    breadcrumbSubtitle: string;
    buttonCopy?: string;
} => {
    const status = import2Import.import2Status;

    switch (status) {
        case 'STATUS_NOT_STARTED':
            return {
                icon: <Logos crmOption={import2Import.crmOption} />,
                pageInfo: `Import your data from ${import2Import.crmOption.label}`,
                pageSubtitle: (
                    <div className={styles.list}>
                        <p>
                            We’re about to launch Import2, our data transfer partner, to make this a
                            breeze. Here’s how it works:
                        </p>
                        <BulletedList
                            size='small'
                            bullets={[
                                <span key='bullet-1'>Start your import with Import2.</span>,
                                <span key='bullet-2'>
                                    They will walk you through getting an API key from your old CRM
                                </span>,
                                <span key='bullet-3'>
                                    Your first 100 contacts & leads will be previewed in Nutshell
                                </span>,
                                <span key='bullet-4'>
                                    Once you’ve checked out the preview, request a full import
                                </span>,
                                <span key='bullet-5'>
                                    We’ll run a full import at no cost when you’re ready to purchase
                                    Nutshell!
                                </span>,
                                <span key='bullet-6'>
                                    Note: Only one Import2 import can be in progress at one time
                                    from your given source CRM to Nutshell. If you would like to
                                    start a new one, you can do this from the Import2 dashboard!
                                </span>,
                            ]}
                        />
                    </div>
                ),
                buttonCopy: 'Start import via Import2',
                breadcrumbSubtitle: 'Go to Import2 to start a sample import',
            };
        case 'STATUS_SAMPLE_IN_PROGRESS':
            return {
                icon: <LoadingIcon />,
                pageInfo: 'Your sample migration is in progress',
                pageSubtitle: (
                    <Body>
                        Awesome! Your sample migration of 100 people and leads is in progress. Check
                        back in a few minutes.
                    </Body>
                ),
                buttonCopy: 'View migration status',
                breadcrumbSubtitle: 'In progress…',
            };
        case 'STATUS_SAMPLE_COMPLETED':
            return {
                icon: <CheckmarkIcon color={colors.green} size={36} />,
                pageInfo: 'Your sample migration is complete',
                pageSubtitle: (
                    <Body>
                        Review your data both in Nutshell and Import2 to make sure it looks right.{' '}
                        <span className='font-bold'>
                            If something looks wrong, you can revert through viewing the migration
                            in Import2 below.
                        </span>
                    </Body>
                ),
                buttonCopy: 'View migration in Import2',
                breadcrumbSubtitle: 'Request approval from Nutshell for full migration',
            };
        case 'STATUS_MIGRATION_REQUESTED':
            return {
                icon: <LoadingIcon />,
                pageInfo: 'Migration request received!',
                pageSubtitle: (
                    <Body>
                        We’ve received your request for a full migration. Give our team time to
                        review and check back later.
                    </Body>
                ),
                buttonCopy: 'View migration in Import2',
                breadcrumbSubtitle: 'Awaiting approval…',
            };
        case 'STATUS_MIGRATION_APPROVED':
            return {
                icon: <CheckmarkIcon color={colors.green} size={36} />,
                pageInfo: 'Migration approved!',
                pageSubtitle: (
                    <Body>
                        You’re good to go! Head over to the Import2 dashboard via the button below
                        to configure and start your full migration.
                    </Body>
                ),
                buttonCopy: 'Go to Import2',
                breadcrumbSubtitle: 'Go to Import2 to start a full import',
            };
        case 'STATUS_MIGRATION_COMPLETE':
            return {
                icon: <CheckmarkIcon color={colors.green} size={36} />,
                pageInfo: 'All done!',
                pageSubtitle: (
                    <Body>
                        Your full migration is complete! Check out your data in Nutshell and
                        Import2.{' '}
                        <span className='font-bold'>
                            If something looks wrong, you can revert by viewing the import in
                            Import2 below.
                        </span>
                    </Body>
                ),
                buttonCopy: 'View your import in Import2',
                breadcrumbSubtitle: 'Complete!',
            };
        case 'STATUS_REVERTED':
            return {
                // @ts-expect-error
                icon: <WarningIcon size={36} color={colors.orangeAttention} />,
                pageInfo: 'Migration was reverted',
                pageSubtitle: (
                    <Body>
                        If this is expected behavior, you can start a new one at any time using the
                        Import2 button below.
                    </Body>
                ),
                buttonCopy: 'View status in Import2',
                breadcrumbSubtitle: 'This import has been reverted',
            };
        case 'STATUS_MIGRATION_IN_PROGRESS':
        default:
            return {
                icon: <LoadingIcon />,
                pageInfo: 'Full migration in progress.',
                pageSubtitle: (
                    <Body>
                        Your full migration has started - this could take a few hours. Check back
                        later for results.
                    </Body>
                ),
                buttonCopy: 'View status in Import2',
                breadcrumbSubtitle: 'In progress…',
            };
    }
};
